<template>
  <div>
    <v-container>
      <base-loader
        v-if="loading"
        :loading="loading"
      />
      <v-flex v-if="!loading && item">
        <v-row>
          <v-col
            xs="12"
            sm="12"
            md="9"
            class="px-md-0"
          >
            <div>
              <h1 class="bpg-admin-page-heading">
                {{ item.name }}{{ item.subtitle && item.subtitle.length ? ' - ' + item.subtitle : '' }}
              </h1>
            </div>
          </v-col>
          <v-col
            xs="12"
            sm="12"
            md="1"
            class="px-md-0"
          >
            <span>
              <div class="d-flex justify-space-between">
                <confirm-dialog
                  v-if="canEdit"
                  title="Force Sync"
                  message="Are you sure you want to queue syncing this product?"
                  @callback="forceSync()"
                >
                  <v-tooltip :disabled="!item.refreshTime">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <base-button
                          text="Force Sync"
                          class="mobile"
                          style="min-width: initial"
                          :disabled="item.refreshTime"
                          :block="false"
                        />
                      </div>
                    </template>
                    <span>This item was queued for sync at {{ item.refreshTime | formatUtcTime }} by {{
                      item.refreshUsername }}. Please wait
                      before queueing again.</span>
                  </v-tooltip>
                </confirm-dialog>
              </div>
            </span>
          </v-col>
        </v-row>

        <v-row class="mt-5">
          <v-col
            xs="12"
            sm="12"
            md="11"
            class="px-md-0"
          >
            <v-card class="bpg-default-card flex-column">
              <v-row class="mt-5">
                <v-col
                  xs="12"
                  sm="12"
                  md="8"
                  class="px-md-0 mr-1"
                >
                  <div class="flex-column  my-3">
                    <span class="font-medium font-weight-bold">Title</span>
                    <v-text-field
                      :value="titleChanged ? item.overrides.title : item.name"
                      class="base-input v-text-no-shadow v-input-restrict-height ml-2"
                      outlined
                      hide-details
                      :disabled="!canEdit"
                      @change="changedTitleOverride($event)"
                    />
                    <div v-if="titleChanged">
                      <span class="font-small">This data has been modified from it's original value. </span>
                      <span
                        v-if="canEdit"
                        class="font-small clickable"
                        @click="resetTitleOverride"
                      >
                        Click here to reset
                      </span>
                    </div>
                  </div>
                  <div class="flex-column my-3">
                    <span class="font-medium font-weight-bold">
                      Subtitle
                    </span>
                    <v-text-field
                      :value="subtitleChanged ? item.overrides.subtitle : item.subtitle"
                      class="base-input v-text-no-shadow v-input-restrict-height ml-2"
                      outlined
                      hide-details
                      :disabled="!canEdit"
                      @change="changedSubtitleOverride($event)"
                    />
                    <div v-if="subtitleChanged">
                      <span class="font-small">This data has been modified from it's original value. </span>
                      <span
                        v-if="canEdit"
                        class="font-small clickable"
                        @click="resetSubtitleOverride"
                      >
                        Click here to reset
                      </span>
                    </div>
                  </div>

                  <div>
                    <span class="font-medium font-weight-bold">Categories</span>
                    <base-loader
                      v-if="categoriesTree.length === 0 || isCategoriesLoading"
                      :loading="categoriesTree.length === 0 || isCategoriesLoading"
                    />
                    <v-chip-group
                      v-else-if="canEdit"
                      column
                      multiple
                      class="ml-2"
                    >
                      <v-chip @click="isCategoryOpen = true">
                        Add Category
                        <v-avatar right>
                          <v-icon>mdi-plus</v-icon>
                        </v-avatar>
                      </v-chip>

                      <v-chip
                        v-for="cat in selectedCategoriesBreadCrumbs"
                        :key="cat.category.id"
                        @click="isCategoryOpen = true"
                      >
                        <div style="line-height: 11px">
                          <div class="font-x-small">
                            {{ cat.crumb }}
                          </div>
                          <div>
                            {{ cat.category.categoryName }}
                          </div>
                        </div>
                      </v-chip>
                    </v-chip-group>

                    <product-categories-tree-modal
                      v-if="categoriesTree"
                      v-model="isCategoryOpen"
                      :categories="categoriesTree"
                      :selected-categories-crumbs="selectedCategoriesBreadCrumbs"
                      @update="onUpdateCategories($event)"
                    />
                  </div>

                  <div class="flex-column">
                    <span class="font-medium font-weight-bold">Flags</span>
                    <base-loader
                      v-if="flagsLoading"
                      :loading="flagsLoading"
                    />
                    <v-chip-group
                      v-else-if="!flagsLoading && (canEdit || item.itemFlags.length)"
                      class="ml-2"
                      column
                      multiple
                    >
                      <v-chip
                        v-for="(itemFlag, i) in item.itemFlags"
                        :key="'permission-' + i"
                        close
                        @click:close="removeFlag(itemFlag.id)"
                      >
                        {{ itemFlag.flag.name }}
                      </v-chip>
                      <v-menu
                        v-if="canEdit"
                        bottom
                        right
                        transition="scale-transition"
                        origin="top left"
                      >
                        <template v-slot:activator="{ on }">
                          <v-chip v-on="on">
                            Add Flag
                            <v-avatar right>
                              <v-icon>mdi-plus</v-icon>
                            </v-avatar>
                          </v-chip>
                        </template>
                        <v-card width="300">
                          <v-list>
                            <v-list-item
                              v-for="(flag, index) in filteredFlagItems"
                              :key="index"
                              @click="saveProductFlag(flag.id)"
                            >
                              <v-list-item-title>{{ flag.name }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-card>
                      </v-menu>
                    </v-chip-group>
                    <div v-else>
                      <span class="ml-2">
                        This item has no flags
                      </span>
                    </div>
                  </div>
                  <div class="flex-column my-3">
                    <span class="font-medium font-weight-bold">Status</span>
                    <base-checkbox
                      v-model="item.deleted"
                      class="my-1 ml-2"
                    >
                      <template v-slot:label>
                        <span class="font-medium">Deleted (Unavailable to sales channels)</span>
                      </template>
                    </base-checkbox>
                    <div class="my-1 ml-2 font-medium">
                      <div v-if="item.isExcluded">
                        <span>This item is not shown on the site due to the value of {{ item.exclusionReasons.length > 1
                          ?
                            'these properties' : 'this property' }}: {{ item.exclusionReasons.join(', ') }}</span>
                      </div>
                      <div v-else-if="item.isUnavailable">
                        <span>This item is shown on the site but is not orderable due to the value of {{
                          item.unavailableReasons.length > 1 ? 'these properties' : 'this property' }}: {{
                          item.unavailableReasons.join(', ') }}</span>
                      </div>
                      <div v-else>
                        <span>This item is shown and available for purchase on the site</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex-column  my-3">
                    <span class="font-medium font-weight-bold">Description Short</span>
                    <quill-editor
                      ref="shortDescriptionEditor"
                      v-model="shortDescriptionContent"
                      :options="editorOption"
                      hide-details
                      :disabled="!canEdit"
                      @change="onShortDescriptionChange($event)"
                    />
                    <div v-if="descriptionShortChanged">
                      <span class="font-small">This data has been modified from it's original value. </span>
                      <span
                        v-if="canEdit"
                        class="font-small clickable"
                        @click="resetDescriptionShortOverride"
                      >
                        Click here to reset
                      </span>
                    </div>
                  </div>
                  <div class="flex-column  my-3">
                    <span class="font-medium font-weight-bold">Description Long</span>
                    <quill-editor
                      ref="longDescriptionEditor"
                      v-model="longDescriptionContent"
                      :options="editorOption"
                      hide-details
                      :disabled="!canEdit"
                      @change="onLongDescriptionChange($event)"
                    />
                    <div v-if="descriptionLongChanged">
                      <span class="font-small">This data has been modified from it's original value. </span>
                      <span
                        v-if="canEdit"
                        class="font-small clickable"
                        @click="resetDescriptionLongOverride"
                      >
                        Click here to reset
                      </span>
                    </div>
                  </div>
                  <div class="flex-column  my-3">
                    <span class="font-medium font-weight-bold">Description Back</span>
                    <quill-editor
                      ref="backDescriptionEditor"
                      v-model="backDescriptionContent"
                      :options="editorOption"
                      hide-details
                      :disabled="!canEdit"
                      @change="onBackDescriptionChange($event)"
                    />
                    <div v-if="descriptionBackChanged">
                      <span class="font-small">This data has been modified from it's original value. </span>
                      <span
                        v-if="canEdit"
                        class="font-small clickable"
                        @click="resetDescriptionBackOverride"
                      >
                        Click here to reset
                      </span>
                    </div>
                  </div>
                  <span class="font-medium font-weight-bold">Media</span>
                  <div class="d-flex mt-3 ml-2">
                    <input
                      ref="imageFileSelector"
                      style="display: none"
                      type="file"
                      accept=".png, .jpeg, .jpg, .PNG, .JPEG, .JPG"
                      @change="filesSelected"
                    >
                    <v-card
                      v-for="image of item.itemMediaFiles"
                      :key="image.id"
                      class="mr-3"
                      outlined
                    >
                      <v-container class="pa-0">
                        <v-row>
                          <v-col cols="auto">
                            <base-image
                              :source="image.cdnLink | fullImagePath('S')"
                              position="left top"
                              max-width="150"
                              contain
                            />
                            <span class="font-medium">{{ image.mediaFileType.name }}</span>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card>
                    <v-card
                      v-for="(image, i) in selectedImages"
                      :key="'image-' + i"
                      class="mr-3"
                      outlined
                    >
                      <v-container
                        class="pa-0"
                        style="height: 100%; min-height: 150px"
                      >
                        <v-col style="height: 100%">
                          <v-row
                            align="end"
                            style="height: 50%"
                          >
                            <span class="truncate">{{ image.name }}</span>
                          </v-row>
                          <v-row
                            align="end"
                            style="height: 50%"
                          >
                            <base-button
                              text="Remove"
                              style="width: 150px"
                              @click="removeImageOnClick"
                            />
                          </v-row>
                        </v-col>
                      </v-container>
                    </v-card>
                    <v-card
                      v-if="!(selectedImages && selectedImages.length)"
                      class="mr-3"
                      outlined
                      @click="$refs.imageFileSelector.click()"
                    >
                      <v-container
                        class="pa-0"
                        style="height: 100%; min-height: 150px"
                      >
                        <v-row
                          align="end"
                          style="height: 50%"
                        >
                          <v-col
                            class="pt-0 pb-0"
                            align="center"
                          >
                            <v-icon style="width: 150px">
                              mdi-camera
                            </v-icon>
                          </v-col>
                        </v-row>
                        <v-row
                          align="start"
                          style="height: 50%"
                        >
                          <v-col
                            class="pt-0 pb-0"
                            align="center"
                          >
                            <span
                              class="font-medium"
                              style="width: 150px"
                            >
                              Add Media
                            </span>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card>
                  </div>
                  <div
                    v-if="uploadFail"
                    class="mt-3 ml-3"
                  >
                    <v-icon color="warning">
                      mdi-alert-octagon
                    </v-icon>
                    <span class="font-small font-weight-bold ml-1 warning--text">Your image could not be saved</span>
                  </div>
                  <div class="my-3 mt-5">
                    <span class="font-medium font-weight-bold">Product purchase message (included with order
                      confirmation)</span>
                    <quill-editor
                      ref="productPurchaseMessageEditor"
                      v-model="productPurchaseMessageContent"
                      :options="editorOption"
                      hide-details
                      :disabled="!canEdit"
                    />
                  </div>
                </v-col>
                <v-col
                  xs="12"
                  sm="12"
                  md="3"
                  class="px-md-0 ml-10"
                >
                  <v-row class="mt-5">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">SKU</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.sku }}</span>
                        </div>
                      </div>
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">ISBN 10</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ showISBN10 }}</span>
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="flex-column  my-3">
                        <span class="font-medium font-weight-bold">Format</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center  font-medium">{{ item.format.displayName ||
                            item.format.name }}</span>
                        </div>
                      </div>
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">ISBN 13</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.books[0].isbn13 ||
                            item.bibles[0].isbn13 }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row>
                    <v-col
                      xs="12"
                      class="px-md-0"
                    >
                      <div class="flex-column  my-3">
                        <span
                          v-if="item.contributorNames && item.contributorNames.length > 1"
                          class="font-medium font-weight-bold"
                        >
                          Contributors
                        </span>
                        <span
                          v-else
                          class="font-medium font-weight-bold"
                        >
                          Contributor(s)
                        </span>
                        <v-chip-group
                          v-if="item.bookContributors && item.bookContributors.length"
                          column
                          multiple
                        >
                          <v-chip
                            v-for="(x, i) in item.bookContributors"
                            :key="'contributor-' + i + '_' + x.ContributorId"
                          >
                            {{ x.fullName }} ({{ x.roleDisplayName }})
                          </v-chip>
                        </v-chip-group>
                        <div v-else>
                          <span class="font-medium">N/A</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="mt-5">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Last Available</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ !item.isUnavailable ? todaysDate :
                            fallBackValue }}</span>
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Qty Available</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.stock ? item.stock.quantity :
                            fallBackValue }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="mt-5">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">MSRP</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.price || fallBackValue
                          }}</span>
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Discount Price</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.basePrice || fallBackValue
                          }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="mt-5">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Page Count</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.books[0].pages ||
                            fallBackValue
                          }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="mt-5">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Group Id</span>
                        <div class="d-flex align-flex-start">
                          <span class="font-weight-extra align-center font-medium">{{ item.itemGroupId }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="mt-2">
                    <v-col
                      xs="5"
                      class="px-md-0"
                    >
                      <div class="my-3">
                        <span class="font-medium font-weight-bold">Related Products</span>
                        <div v-if="item.relatedItems">
                          <div
                            v-for="prd in item.relatedItems"
                            :key="prd.id"
                          >
                            <router-link
                              v-if="prd.id !== item.id"
                              :to="`/products/${prd.id}/edit`"
                            >
                              {{ prd.sku }} - {{ prd.formatDisplayName }}
                            </router-link>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <product-endorsments
                :is-loading="endorsements === null"
                :endorsements="endorsements ? endorsements : []"
                @addEndorsement="onAddEndorsement($event)"
                @deleteEndorsement="onDeleteEndorsement($event)"
                @editEndorsement="onEditEndorsement($event)"
                @reorder="onReorderEndorsements($event)"
              />

              <ProductFlexibleMedia
                :media="associatedMedia"
                :is-loading="false"
                @addMedia="onAddMedia($event)"
                @editMedia="onEditMedia($event)"
                @deleteMedia="onDeleteMedia($event)"
                @reorder="onReorderMedia($event)"
              />

              <ProductExcerpt
                :product-excerpt="productExcerpt"
                :is-loading="productExcerpt === null"
                :product-id="item.id"
                @deleteExcerpt="onDeleteProductExcerpt($event)"
                @addProductExcerpt="onAddProductExcerpt($event)"
              />

              <ProductPrintOnDemand
                :is-loading="loadingPrintOnDemand"
                :print-on-demands="printOnDemands || []"
                :print-on-demand-title-override="printOnDemandTitleOverride"
                @createOrUpdate="onUpdatePrintOnDemand($event)"
                @deletePrintOnDemand="onDeletePrintOnDemand($event)"
                @saveTitle="onUpdatePrintOnDemandTitle($event)"
              />
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mt-5">
          <v-col
            xs="12"
            sm="12"
            md="9"
            class="px-0"
          >
            <div class="d-flex justify-space-between my-3">
              <base-button
                text="Cancel"
                background-color="none"
                class="mobile"
                style="min-width: initial"
                :block="false"
                @click="cancel()"
              />
              <div class="d-flex">
                <confirm-dialog
                  v-if="canEdit"
                  title="Save Changes"
                  message="Are you sure you want to save this product?"
                  @callback="saveChanges()"
                >
                  <base-button
                    text="Save"
                    class="mobile ml-3"
                    style="min-width: initial"
                    :block="false"
                  />
                </confirm-dialog>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import $http from "@/plugins/axios"
import { CATEGORIES_TYPES } from '../../store/modules/categories'
import { quillEditor } from 'vue-quill-editor'
import { mapGetters, mapState, mapActions } from 'vuex'
import { AUTH_TYPES } from '@/store/modules/auth'
import { ChapterPermissions } from '@/utils/enums/security-enums'
import ConfirmDialog from '@/components/dialogs/confirm-dialog'
import ProductCategoriesTreeModal from '../../components/pages/products/product-categories-tree-modal.vue'
import * as cloneDeep from 'lodash.clonedeep'
import ProductEndorsments from '../../components/pages/products/product-endorsments.vue'
import ProductFlexibleMedia from '../../components/pages/products/product-flexible-media.vue'
import ProductPrintOnDemand from '../../components/pages/products/product-print-on-demand.vue'
import ProductExcerpt from '../../components/pages/products/product-excerpt.vue'
export default {
  name: 'EditProduct',
  components: { ConfirmDialog, quillEditor, ProductCategoriesTreeModal, ProductEndorsments, ProductFlexibleMedia, ProductPrintOnDemand, ProductExcerpt },
  data () {
    return {
      fallBackValue: "N/A",
      resetting: false,
      loading: false,
      item: {},
      flagComboModel: null,
      flags: [],
      flagsLoading: false,
      endorsements: null,
      associatedMedia: null,
      productExcerpt: null,
      printOnDemands: [],
      loadingPrintOnDemand: true,
      printOnDemandTitleOverride: 'Available at other Retailers',
      categoriesLoading: false,
      isCategoriesLoading: false,
      titleChanged: false,
      subtitleChanged: false,
      descriptionLongChanged: false,
      descriptionShortChanged: false,
      descriptionBackChanged: false,
      selectedImages: [],
      uploadFail: false,
      shortDescriptionContent: '',
      longDescriptionContent: '',
      backDescriptionContent: '',
      productPurchaseMessageContent: '',
      isCategoryOpen: false,
      editorOption: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block', 'link'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'align': [] }]
          ]
        }
      }
    }
  },
  computed: {
    ...mapState({
      categoriesTree: state => state.categories.categoriesTree
    }),
    ...mapGetters({
      hasPermission: AUTH_TYPES.getters.hasPermission
    }),
    canEdit () {
      return this.hasPermission(ChapterPermissions.CanEditProducts)
    },
    filteredFlagItems () {
      return this.flags
        .filter(f => !this.item.itemFlags.find(pf => pf.flag.id === f.id))
    },
    editor () {
      return this.$refs.myQuillEditor ? this.$refs.myQuillEditor.quill : null
    },
    todaysDate () {
      return new Date().toDateString()
    },
    selectedCategories () {
      const map = this.getFlattenedTree(this.categoriesTree, {})
      return this.item?.itemCategories?.filter(x => map[x.categoryId]).map(x => x.category) ?? []
    },
    selectedCategoriesBreadCrumbs () {
      if (this.categoriesTree?.length === 0) return []

      const map = this.getFlattenedTree(this.categoriesTree, {})

      const crumbs = []

      this.selectedCategories.forEach(s => {
        let crumb = this.getBreadCrumb(map, s, '')
        const split = crumb.split('/')
        split.pop()
        crumb = split.join('/')
        crumbs.push({ category: s, crumb: crumb })
      })
      return crumbs
    },
    showISBN10 () {
      if (this.item.books && this.item.books.length > 0) {
        return this.item.books[0].isbn10
      } else {
        if (this.item.bibles && this.item.bibles.length > 0) {
          return this.item.bibles[0].isbn10
        }
      }
      return ""
    }
  },
  watch: {
    $route: {
      deep: true,
      handler () {
        this.initItemData()
      }
    }
  },
  async created () {
    this.initItemData()
  },
  methods: {
    ...mapActions({
      fetchCategories: CATEGORIES_TYPES.actions.fetchCategories
    }),
    async initItemData () {
      this.loading = true

      await Promise.all([
        $http().get(`admin/items/${this.$route.params.id}`)
          .then((res) => {
            this.item = res
            // changed
            this.titleChanged = this.item.overrides.title != null
            this.subtitleChanged = this.item.overrides.subtitle != null
            this.descriptionShortChanged = this.item.overrides.descriptionShort != null
            this.descriptionLongChanged = this.item.overrides.descriptionLong != null
            this.descriptionBackChanged = this.item.overrides.descriptionBack != null
            // content
            this.shortDescriptionContent = this.descriptionShortChanged ? this.item.overrides.descriptionShort : this.item.descriptionShort
            this.longDescriptionContent = this.descriptionLongChanged ? this.item.overrides.descriptionLong : this.item.descriptionLong
            this.backDescriptionContent = this.descriptionBackChanged ? this.item.overrides.descriptionBack : this.item.descriptionBack
            if (this.item && this.item.overrides && this.item.overrides.productPurchaseMessage != null) this.productPurchaseMessageContent = this.item.overrides.productPurchaseMessage
          })
      ]).finally(() => {
        this.loading = false
        this.fetchFlags()
        this.fetchEndorsements(this.item.sku)
        this.fetchAssociatedMedia(this.item.sku)
        this.fetchPrintOnDemand(this.item.sku)
        this.fetchProductExcerpt(this.item.sku)
      })
      this.fetchCategories()
    },
    async fetchEndorsements (sku) {
      this.endorsements = (await $http().get(`admin/products/endorsements?sku=${sku}`))
      if (this.endorsements) {
        this.endorsements = this.endorsements.sort((a, b) => a.sortOrder - b.sortOrder)
      } else {
        this.endorsements = []
      }
    },
    async fetchFlags () {
      this.flags = (await $http().get('admin/flags'))
    },
    async fetchAssociatedMedia (sku) {
      this.associatedMedia = (await $http().get(`admin/associatedMedia/get/${sku}`))
      if (this.associatedMedia) {
        this.associatedMedia = this.associatedMedia.sort((a, b) => a.order - b.order)
      } else {
        this.associatedMedia = []
      }
    },
    async fetchProductExcerpt (sku) {
      this.productExcerpt = (await $http().get(`admin/items/excerpt/${sku}`))
      if (!this.productExcerpt) {
        this.productExcerpt = { id: -1 }
      }

      this.productExcerpt.sku = this.item.sku
    },
    async fetchPrintOnDemand (sku) {
      this.loadingPrintOnDemand = true
      const result = await $http().get(`admin/products/printOnDemand/${sku}`)
      this.printOnDemands = result.printOnDemands || []
      this.printOnDemandTitleOverride = result.printOnDemandTitleOverride?.overrideText || 'Available at other Retailers'
      this.loadingPrintOnDemand = false
    },
    async onDeletePrintOnDemand (id) {
      this.loadingPrintOnDemand = true
      this.printOnDemand = null
      this.printOnDemands = this.printOnDemands.filter(x => x.id !== id)
      await $http().delete(`admin/products/printOnDemand/${id}`)
      this.loadingPrintOnDemand = false
    },
    async onUpdatePrintOnDemandTitle (title) {
      this.loadingPrintOnDemand = true
      const result = await $http().put(`admin/products/printOnDemand/overrideTitle/${this.item.sku}?text=${title}`)
      this.printOnDemandTitleOverride = result.overrideText || 'Available at other Retailers'
      this.loadingPrintOnDemand = false
    },
    async onUpdatePrintOnDemand (printOnDemand) {
      this.loadingPrintOnDemand = true

      if (printOnDemand.id) {
        // updating existing
        const res = (await $http().put(`admin/products/updatePrintOnDemand`, printOnDemand))
        this.printOnDemands = this.printOnDemands.map(x => {
          if (x.id === printOnDemand.id) {
            return res
          }
          return x
        })
      } else {
        // creating new
        this.printOnDemands.push((await $http().post(`admin/products/createPrintOnDemand/${this.item.sku}`, printOnDemand)))
      }

      this.loadingPrintOnDemand = false
    },
    async onAddEndorsement (endorsement) {
      const placeholder = { ...endorsement, id: -1 }
      this.endorsements.unshift(placeholder)
      const newEndorsement = await $http().post(`admin/products/addEndorsementBySku`, {
        endorsementModel: endorsement,
        sku: this.item.sku
      })
      this.endorsements.splice(this.endorsements.indexOf(placeholder), 1, newEndorsement)
    },
    async onAddMedia (newMedia) {
      newMedia.append('productId', this.item.id)
      const placeholder = { id: -1, title: newMedia.get('title'), subtitle: newMedia.get('subtitle'), shortDescription: newMedia.get('shortDescription'), imageFileUrl: '' }
      this.associatedMedia.unshift(placeholder)
      var createdMedia = await $http().post(`admin/associatedMedia`, newMedia)
      this.associatedMedia.splice(this.associatedMedia.indexOf(placeholder), 1, createdMedia)
    },
    async onAddProductExcerpt (excerptData) {
      this.productExcerpt = null

      excerptData.productId = this.item.id
      const formData = new FormData()
      for (const key in excerptData) {
        if (excerptData[key]) {
          formData.append(key, excerptData[key])
        }
      }
      this.productExcerpt = await $http().post(`admin/items/save-excerpt`, formData)
      this.productExcerpt.sku = this.item.sku
    },
    async onDeleteEndorsement (endorsement) {
      this.endorsements = this.endorsements.filter(x => x.id !== endorsement.id)

      await $http().delete(`admin/products/endorsement?id=${endorsement.id}`)
    },
    async onEditEndorsement (endorsement) {
      this.endorsements = this.endorsements.map(e => {
        if (e.id === endorsement.id) {
          return endorsement
        } else {
          return e
        }
      })
      await $http().put(`admin/products/endorsement`, { ...endorsement })
    },
    async onEditMedia (media) {
      const id = parseInt(media.get('id'), 10)
      const m = this.associatedMedia.find(x => x.id === id)
      m.id = -1
      const newMedia = await $http().patch(`admin/associatedMedia/update`, media)
      this.associatedMedia.splice(this.associatedMedia.indexOf(m), 1, newMedia)
    },
    async onDeleteMedia (mediaId) {
      this.associatedMedia = this.associatedMedia.filter(x => x.id !== mediaId)
      await $http().delete(`admin/associatedMedia/${mediaId}`)
    },
    async onDeleteProductExcerpt (excerptId) {
      this.productExcerpt = null
      await $http().delete(`admin/items/excerpt/delete/${excerptId}`)
      this.productExcerpt = { id: -1, sku: this.item.sku }
    },
    async onReorderEndorsements (endorsements) {
      this.endorsements = endorsements
      await $http().patch(`admin/products/reorderEndorsements`, { endorsements })
    },
    async onReorderMedia (media) {
      this.associatedMedia = media
      await $http().patch(`admin/associatedMedia/reorder`, { medias: media })
    },
    onShortDescriptionChange ({ quicll, html, text }) {
      if (!this.resetting) this.changedDescriptionShortOverride(html)
      this.resetting = false
    },
    onLongDescriptionChange ({ quill, html, text }) {
      if (!this.resetting) this.changedDescriptionLongOverride(html)
      this.resetting = false
    },
    onBackDescriptionChange ({ quill, html, text }) {
      if (!this.resetting) this.changedDescriptionBackOverride(html)
      this.resetting = false
    },
    changedTitleOverride (input) {
      this.item.overrides.title = input
      this.titleChanged = true
    },
    changedSubtitleOverride (input) {
      this.item.overrides.subtitle = input
      this.subtitleChanged = true
    },
    changedDescriptionShortOverride (input) {
      this.item.overrides.descriptionShort = input
      this.descriptionShortChanged = true
    },
    changedDescriptionLongOverride (input) {
      this.item.overrides.descriptionLong = input
      this.descriptionLongChanged = true
    },
    changedDescriptionBackOverride (input) {
      this.item.overrides.descriptionBack = input
      this.descriptionBackChanged = true
    },
    formatDate (date) {
      return new Date(date).toDateString()
    },
    resetTitleOverride () {
      this.item.overrides.title = this.item.name
      this.titleChanged = false
    },
    resetSubtitleOverride () {
      this.item.overrides.subtitle = this.item.subtitle
      this.subtitleChanged = false
    },
    resetDescriptionShortOverride () {
      this.resetting = true
      this.item.overrides.descriptionShort = this.item.descriptionShort
      this.shortDescriptionContent = this.item.descriptionShort
      this.descriptionShortChanged = false
    },
    resetDescriptionLongOverride () {
      this.resetting = true
      this.item.overrides.descriptionLong = this.item.descriptionLong
      this.longDescriptionContent = this.item.descriptionLong
      this.descriptionLongChanged = false
    },
    resetDescriptionBackOverride () {
      this.resetting = true
      this.item.overrides.descriptionBack = this.item.descriptionBack
      this.backDescriptionContent = this.item.descriptionBack
      this.descriptionBackChanged = false
    },
    async saveProductFlag (id) {
      const model = {
        flagId: id,
        itemId: this.item.id
      }
      this.flagsLoading = true
      return $http().post(`admin/items/${this.$route.params.id}/flags`, model)
        .then((res) => {
          this.item.itemFlags.push(res)
          this.$nextTick(() => {
            this.flagComboModel = null
          })
        }).finally(() => { this.flagsLoading = false })
    },
    async saveProductCategory (id) {
      const model = {
        categoryId: id,
        itemId: this.item.id
      }
      this.categoriesLoading = true
      return $http().post(`admin/items/${this.$route.params.id}/categories`, model)
        .then((res) => {
          this.item.itemCategories.push(res)
          this.$nextTick(() => {
            this.CategoryComboModel = null
          })
        }).finally(() => { this.categoriesLoading = false })
    },
    async removeFlag (id) {
      this.flagsLoading = true
      return $http().delete(`admin/items/${this.$route.params.id}/flags/${id}`)
        .then(() => {
          this.item.itemFlags = this.item.itemFlags.filter(f => f.id !== id)
        }).finally(() => { this.flagsLoading = false })
    },
    async removeCategory (id) {
      this.categoriesLoading = true
      return $http().delete(`admin/items/${this.$route.params.id}/flags/${id}`)
        .then(() => {
          this.item.itemFlags = this.item.itemFlags.filter(f => f.id !== id)
        }).finally(() => { this.flagsLoading = false })
    },
    removeImageOnClick () {
      this.selectedImages = []
      this.$refs.imageFileSelector.value = null
    },
    filesSelected (event) {
      const files = event.target.files
      for (let i = 0; i < files.length; i++) {
        this.selectedImages.push(files[i])
      }
    },
    async forceSync () {
      this.loading = true
      try {
        let res = await $http().post(`admin/items/${this.$route.params.id}/refresh`)
        this.item = res
      } finally {
        this.loading = false
      }
    },
    async saveChanges () {
      this.loading = true
      if (!this.titleChanged) {
        this.item.overrides.title = null
      }
      if (!this.subtitleChanged) {
        this.item.overrides.subtitle = null
      }
      if (!this.descriptionLongChanged) {
        this.item.overrides.descriptionLong = null
      }
      if (!this.descriptionShortChanged) {
        this.item.overrides.descriptionShort = null
      }
      if (!this.descriptionBackChanged) {
        this.item.overrides.descriptionBack = null
      }
      if (this.productPurchaseMessageContent.length > 0) {
        if (this.item.overrides == null) this.item.overrides = {}
        this.item.overrides.productPurchaseMessage = this.productPurchaseMessageContent
      }
      if (this.selectedImages && this.selectedImages.length) {
        const formData = new FormData()
        formData.append('file', this.selectedImages[0], this.selectedImages[0].name)
        await $http().post(`admin/items/${this.$route.params.id}/images`, formData)
          .then((res) => { this.uploadFail = !res })
      }
      this.item.overrides.deleted = this.item.deleted
      await $http().patch(`admin/items/${this.$route.params.id}`, this.item.overrides)
        .then((res) => {
          this.item = res
        })
        .finally(() => {
          this.loading = false
          this.selectedImages = []
        })
    },
    cancel () {
      this.$router.push('/products')
    },
    getFlattenedTree (tree, map) {
      tree.forEach(leaf => {
        map[leaf.id] = leaf
        this.getFlattenedTree(leaf.subCategories, map)
      })

      return map
    },
    getBreadCrumb (map, cat, str) {
      if (!cat.parentId) {
        return cat.categoryName
      }
      return this.getBreadCrumb(map, map[cat.parentId], str) + ' / ' + cat.categoryName
    },
    async onUpdateCategories (categories) {
      this.isCategoriesLoading = true
      categories = cloneDeep(categories)

      const parsed = categories.map(x => {
        x.category.subCategories = []
        return x.category
      })

      const existing = this.item.itemCategories

      const toAdd = parsed.filter(x => existing.some(y => y.categoryId === x.id) === false).map(x => {
        return {
          id: -1,
          categoryId: x.id,
          itemId: this.item.id
        }
      })
      const toRemove = existing.filter(x => parsed.some(y => y.id === x.categoryId) === false).map(x => {
        return {
          id: x.id,
          categoryId: x.categoryId,
          itemId: this.item.id
        }
      })

      if (toAdd.length === 0 && toRemove.length === 0) {
        this.isCategoriesLoading = false
        return
      }

      await $http().post(`/admin/items/updateCategoriesOnItem`, {
        toAdd: toAdd,
        toRemove: toRemove,
        itemId: this.item.id
      })

      await $http().get(`admin/items/${this.$route.params.id}`)
        .then((res) => {
          this.item = res
        })

      this.isCategoriesLoading = false
    }
  }
}
</script>

<style scoped>
.truncate {
  display: block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
